import React from "react";
import { Send, Paperclip, Loader2, Image as ImageIcon, Video, Music, File, X } from "lucide-react";
import { AlertCircle } from "lucide-react";

/**
 * MessageInput Component - Handles message input and file uploads
 */
const MessageInput = ({
  messageText,
  setMessageText,
  selectedFile,
  filePreview,
  isWindowOpen,
  sending,
  uploadingMedia,
  onFileSelect,
  onRemoveFile,
  onSendMessage,
}) => {
  return (
    <div className="p-3 border-t border-gray-200 bg-white">
      {!isWindowOpen && (
        <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
          ⚠️ Conversation window closed. Use template messages to contact this customer.
        </div>
      )}
      
      {/* File Preview */}
      {filePreview && selectedFile && (
        <div className="mb-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {filePreview.type === "image" && (
                <>
                  <ImageIcon size={20} className="text-primary flex-shrink-0" />
                  <img src={filePreview.url} alt="Preview" className="h-12 w-12 object-cover rounded flex-shrink-0" />
                </>
              )}
              {filePreview.type === "video" && (
                <>
                  <Video size={20} className="text-primary flex-shrink-0" />
                  <video src={filePreview.url} className="h-12 w-12 object-cover rounded flex-shrink-0" />
                </>
              )}
              {filePreview.type === "audio" && (
                <>
                  <Music size={20} className="text-primary flex-shrink-0" />
                  <audio src={filePreview.url} controls className="flex-1 max-w-xs" />
                </>
              )}
              {filePreview.type === "document" && (
                <>
                  <File size={20} className="text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{filePreview.name}</p>
                    <p className="text-xs text-gray-500">{(filePreview.size / 1024).toFixed(2)} KB</p>
                  </div>
                </>
              )}
              <div className="flex-1 min-w-0 ml-2">
                <p className="text-sm font-medium text-gray-700 truncate">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(2)} KB</p>
              </div>
            </div>
            <button
              onClick={onRemoveFile}
              className="ml-2 p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
              type="button"
            >
              <X size={16} className="text-gray-600" />
            </button>
          </div>
        </div>
      )}

      <form onSubmit={onSendMessage} className="flex items-center gap-2">
        {/* File Input Button */}
        <label className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <Paperclip size={20} />
          <input
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
            onChange={onFileSelect}
            disabled={!isWindowOpen || sending || uploadingMedia}
            className="hidden"
          />
        </label>
        
        <input
          type="text"
          placeholder={isWindowOpen ? (selectedFile ? "Add a caption (optional)..." : "Type a message...") : "Window closed - use templates"}
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          disabled={!isWindowOpen || sending || uploadingMedia}
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        <button
          type="submit"
          disabled={!isWindowOpen || sending || uploadingMedia || (!messageText.trim() && !selectedFile)}
          className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {(sending || uploadingMedia) ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Send size={16} />
          )}
        </button>
      </form>
    </div>
  );
};

export default MessageInput;

